import type { GuardianPlayer } from '@/features/guardian/services/get-guardian-players';
import {
  INITIAL_ADD_PLAYER_FORM,
  type AddPlayerForm,
  type DominantHand,
  type NeedClubs,
} from '@/features/onboarding/schemas/add-player-schema';
import type { PendingPlayer } from '@/features/onboarding/stores/onboarding-players-store';
import { countryNameToCode } from '@/features/onboarding/utils/country-helpers';
import { normalizePlayerGender } from '@/utils/player-gender';

/** Height display: first 2 digits + inch mark (e.g. `48"`); empty stays empty. Mirrors coach. */
export const formatHeightInchesDisplay = (raw: string | undefined): string => {
  const digits = (raw ?? '').replace(/\D/g, '').slice(0, 2);
  return digits ? `${digits}"` : '';
};

/** Local calendar date as `YYYY-MM-DD` for the API's `dob` (no UTC shift). */
export const toDateOnly = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Parse a `YYYY-MM-DD` `dob` back into a local Date (mirrors `toDateOnly`; no UTC
 *  shift, unlike `new Date('YYYY-MM-DD')`). Returns null for empty/invalid input. */
export const fromDateOnly = (value: string | undefined): Date | null => {
  const [year, month, day] = (value ?? '').split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

/** Map a queued player back into the form, for the edit sheet. */
export const playerToForm = ({ payload, sameAddress }: PendingPlayer): AddPlayerForm => ({
  firstName: payload.firstname,
  lastName: payload.lastname,
  dob: fromDateOnly(payload.dob),
  gender: normalizePlayerGender(payload.gender),
  height: payload.height != null ? formatHeightInchesDisplay(String(payload.height)) : '',
  needClubs: payload.needClubs,
  dominantHand: payload.dominantHand,
  // Restore the "Same address with me" checkbox; the address fields stay populated
  // either way so the rebuilt payload keeps the right address on save.
  sameAddress,
  country: countryNameToCode(payload.address?.country),
  city: payload.address?.city ?? '',
  state: payload.address?.state ?? null,
  zipCode: payload.address?.zipcode ?? '',
  avatar: payload.avatarUrl ? { uri: payload.avatarUrl } : null,
});

/** Prefill the form from an already-saved server player, for the read-only view sheet.
 *  The API player only carries identity fields (name/dob/gender/avatar/level), so the
 *  rest fall back to the form defaults. `dob` may be a full ISO timestamp — keep only
 *  the date part so `fromDateOnly` parses it. */
export const guardianPlayerToForm = (player: GuardianPlayer): AddPlayerForm => ({
  ...INITIAL_ADD_PLAYER_FORM,
  firstName: player.firstname ?? '',
  lastName: player.lastname ?? '',
  dob: fromDateOnly(player.dob?.slice(0, 10)),
  gender: normalizePlayerGender(player.gender),
  avatar: player.avatarUrl ? { uri: player.avatarUrl } : null,
  country: countryNameToCode(player.address?.country),
  city: player.address?.city ?? '',
  state: player.address?.state ?? '',
  zipCode: player.address?.zipcode ?? '',
  height: player.height ? formatHeightInchesDisplay(String(player.height)) : '',
  dominantHand: (player.dominantHand ?? 'right') as DominantHand,
  needClubs: (player.needClubs ?? 'no') as NeedClubs,
});
